import { DgaFrequency } from "@/__generated__/graphql";
import { useFormatters } from "@/hooks/useFormatters";
import { useSuspenseQuery } from "@apollo/client";
import ModalTitle from "@components/modal/ModalTitle";
import { DGA_REPORTS_TABLE_GET_DEVICE } from "@features/dga/graphql/queries/getDGAReportsForTable";
import { getHourlyChartOption, getCalendarChartOption } from "@features/dga/utils/chartOptions";
import Echarts from "@features/echarts";
import { msg } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { DialogContent } from "@mui/material";
import { Box } from "@mui/system";
import dayjs from "dayjs";
import { FC, useTransition, useMemo, useCallback } from "react";
import { WeekSelector } from "./components/WeekSelector";
import { useWeekRange } from "@/hooks/useWeekRange";

interface Props {
  deviceId: string;
  onClose: () => void;
}

export const ChartContent: FC<Props> = ({ deviceId, onClose }) => {
  const { i18n, _ } = useLingui();
  const formatters = useFormatters();
  const [isPending, startTransition] = useTransition();

  const weekControls = useWeekRange(formatters.ianaTimezone);
  const { selectedDate, range, previousWeek, nextWeek, setWeekByDate } = weekControls;

  const currDate = dayjs();
  const isNextWeekDisabled = range.stopTs > currDate.unix();
 

  const { data } = useSuspenseQuery(DGA_REPORTS_TABLE_GET_DEVICE, {
    variables: {
      deviceId,
      reportsInput: {
        start: range.startTs,
        stop: range.stopTs,
      },
    },
  });

  const frequency = data.device.dga?.configuration.frequency;


  const option = useMemo(() => {
    if (!data.device.dga || !data.device.dga.configuration) return {};
    const reports = data.device.dga.reports.items;
    const { type, frequency } = data.device.dga.configuration;
    if (frequency === DgaFrequency.Hourly)
      return getHourlyChartOption(reports, range.startTs, type, formatters, i18n);
    if (frequency === DgaFrequency.Daily)
      return getCalendarChartOption(reports, type, formatters, i18n, 3, 'horizontal');
    if (frequency === DgaFrequency.Weekly)
      return getCalendarChartOption(reports, type, formatters, i18n, 12, 'vertical');
    if (frequency === DgaFrequency.Monthly)
      return getCalendarChartOption(reports, type, formatters, i18n, 12, 'vertical');
    return null;
  }, [data, range.startTs, formatters, i18n]);


  const frequencyPropertiesMap = {
    [DgaFrequency.Hourly]: {
      subtitle: _(msg`Frecuencia de reporte cada una hora`),
      height: 500,
    },
    [DgaFrequency.Daily]: {
      subtitle: _(msg`Frecuencia de reporte diaria`),
      height: 300,
    },
    [DgaFrequency.Weekly]: {
      subtitle: _(msg`Frecuencia de reporte semanal`),
      height: 500,
    },
    [DgaFrequency.Monthly]: {
      subtitle: _(msg`Frecuencia de reporte mensual`),
      height: 500,
    },
  };

  const { subtitle, height } = (frequency && frequencyPropertiesMap[frequency]) || {
    subtitle: undefined,
    height: 500,
  };

  const handleWeekChange = useCallback((newRange: {startTs: number, stopTs: number}) => {
    startTransition(() => {
      setWeekByDate(dayjs.unix(newRange.startTs));
    });
  }, [setWeekByDate]);

  return (
    <>
      <ModalTitle
        onClose={onClose}
        title={_(msg`Gráfico de reportes DGA ${data.device.profile.name}`)}
        subtitle={subtitle}
      />
      <DialogContent dividers>
        {frequency === DgaFrequency.Hourly && (
          <WeekSelector
            selectedDate={selectedDate}
            onPrevious={() => startTransition(previousWeek)}
            onNext={() => startTransition(nextWeek)}
            onDateChange={(range) => handleWeekChange(range)}
            disableNext={isNextWeekDisabled}
          />
        )}
        <Box height={height} overflow="hidden" sx={{ opacity: isPending ? 0.5 : 1 }}>
          {option && <Echarts option={option} />}
        </Box>
      </DialogContent>
    </>
  );
};
